/**
 * Turns the flat leaf rows the rollup endpoints return into a nested list of display rows (group
 * subtotals + indented leaves), so the read-only rollup views can render a clean nested MUI table the
 * same way "By period & node" does — no data-grid component. Group order follows leaf insertion order,
 * so the caller controls sorting by pre-sorting the leaves.
 */

import { cleanFloat } from './planEntryRows';

/** One per-unit quantity bucket. A null unitId is the "unitless" bucket (lines saved without a stamped unit). */
export interface UnitQuantity {
  unitId: string | null;
  quantity: number;
}

export interface RollupLeaf {
  keys: string[];
  amount: number;
  /** Per-unit quantities, sparse — empty when the leaf carries no quantity data. */
  quantities: UnitQuantity[];
}

export interface RollupDisplayRow {
  /** Unique path (ancestor labels joined) — used for collapse and React keys. */
  path: string;
  depth: number;
  label: string;
  /** True for a grouping level (has children); false for a terminal leaf. */
  isGroup: boolean;
  childCount: number;
  amount: number;
  /**
   * Per-unit quantity sums over the subtree, never merged across units ("5 ha + 40 tim" is not a
   * number — spec §12.4). First-appearance order from the leaves, the unitless bucket last.
   */
  quantities: UnitQuantity[];
  /** Weighted-average unit price (amount / quantity) only when the subtree is unit-uniform (one non-zero bucket); null otherwise. */
  unitPrice: number | null;
}

interface Node {
  label: string;
  children: Map<string, Node>;
  leaves: RollupLeaf[];
}

const SEP = '';

export function buildRollupRows(leaves: RollupLeaf[]): RollupDisplayRow[] {
  const root: Node = { label: '', children: new Map(), leaves: [] };
  for (const leaf of leaves) {
    let node = root;
    for (const k of leaf.keys) {
      let child = node.children.get(k);
      if (!child) {
        child = { label: k, children: new Map(), leaves: [] };
        node.children.set(k, child);
      }
      node = child;
    }
    node.leaves.push(leaf);
  }

  // Per-unit buckets (a JS Map allows the null key — the unitless bucket), mirroring sumByUnit's
  // semantics in planEntryRows: buckets accumulate independently and are NEVER summed with each
  // other. Insertion order = first appearance in leaf order (leaves arrive server-sorted).
  function agg(node: Node): { amount: number; buckets: Map<string | null, number> } {
    let amount = 0;
    const buckets = new Map<string | null, number>();
    const add = (unitId: string | null, quantity: number) =>
      buckets.set(unitId, (buckets.get(unitId) ?? 0) + quantity);
    for (const l of node.leaves) {
      amount += l.amount;
      for (const q of l.quantities) add(q.unitId, q.quantity);
    }
    for (const c of node.children.values()) {
      const r = agg(c);
      amount += r.amount;
      for (const [unitId, quantity] of r.buckets) add(unitId, quantity);
    }
    return { amount, buckets };
  }

  /** Buckets → display list: float-cleaned, first-appearance order, the unitless bucket last. */
  function toQuantities(buckets: Map<string | null, number>): UnitQuantity[] {
    const out: UnitQuantity[] = [];
    for (const [unitId, quantity] of buckets) {
      if (unitId !== null) out.push({ unitId, quantity: cleanFloat(quantity) });
    }
    if (buckets.has(null)) out.push({ unitId: null, quantity: cleanFloat(buckets.get(null) ?? 0) });
    return out;
  }

  const out: RollupDisplayRow[] = [];
  function walk(node: Node, depth: number, path: string) {
    for (const child of node.children.values()) {
      const p = path ? `${path}${SEP}${child.label}` : child.label;
      const isGroup = child.children.size > 0;
      const a = agg(child);
      const quantities = toQuantities(a.buckets);
      // Pris only where the subtree is unit-uniform: exactly one bucket, non-zero. A mixed-unit
      // average (amount / "5 ha + 40 tim") is not a number.
      const uniform = quantities.length === 1 ? quantities[0].quantity : null;
      out.push({
        path: p,
        depth,
        label: child.label,
        isGroup,
        childCount: child.children.size,
        amount: a.amount,
        quantities,
        unitPrice: uniform ? a.amount / uniform : null,
      });
      if (isGroup) walk(child, depth + 1, p);
    }
  }
  walk(root, 0, '');
  return out;
}

/** Whether a row is hidden because one of its ancestors is collapsed. */
export function isHiddenByCollapse(path: string, collapsed: Set<string>): boolean {
  for (const c of collapsed) {
    if (path !== c && path.startsWith(c + SEP)) return true;
  }
  return false;
}
